import { createPortal } from "react-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { LENIS_SCROLL_EVENT } from "@/lenisScrollBridge";
import { Menu, X } from "lucide-react";
import sparklHeaderLogoUrl from "@/assets/sparkl-header-logo-black.svg";

function getStudentLoginUrl() {
  if (typeof window === "undefined") return "https://student.sparkl.me/login";
  const h = window.location.hostname.toLowerCase();
  if (h.includes("sparkl.ac")) return "https://student.sparkl.ac/login";
  return "https://student.sparkl.me/login";
}

const NAV_LINKS = [
  { href: "/", label: "Home", testId: "nav-link-home" },
  { href: "/about", label: "About", testId: "nav-link-about" },
  { href: "/courses", label: "Courses", testId: "nav-link-courses" },
];

function isNavLinkActive(href) {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  if (href === "/") return path === "/" || path === "";
  if (href === "/courses") {
    return path === "/courses" || path.startsWith("/courses/");
  }
  if (href === "/about") {
    return path === "/about" || path.startsWith("/about/");
  }
  return path === href.replace(/\/$/, "");
}

/** Always show bar near top of document */
const SCROLL_TOP_PIN_PX = 56;
/** Upward travel required before revealing nav after it was hidden */
const NAV_REVEAL_UP_PX = 44;

export default function SiteNavbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  /** Web + mobile: hide while scrolling down; show while scrolling up (except near top) */
  const [navConcealedByScroll, setNavConcealedByScroll] = useState(false);
  const concealLockedRef = useRef(false);
  const hideAtScrollRef = useRef(0);

  /**
   * Hide on Lenis direction down; reveal only after a real upward scroll (not
   * smooth-scroll jitter during fast flick / post-release coast).
   */
  useEffect(() => {
    const reduceMq = window.matchMedia("(prefers-reduced-motion: reduce)");

    const apply = (scrollY, direction) => {
      if (reduceMq.matches) {
        concealLockedRef.current = false;
        setNavConcealedByScroll(false);
        return;
      }

      if (scrollY <= SCROLL_TOP_PIN_PX) {
        concealLockedRef.current = false;
        setNavConcealedByScroll(false);
        return;
      }

      if (direction === 1) {
        concealLockedRef.current = true;
        hideAtScrollRef.current = scrollY;
        setNavConcealedByScroll(true);
        return;
      }

      if (direction === -1) {
        const scrolledUp = hideAtScrollRef.current - scrollY;
        if (!concealLockedRef.current || scrolledUp >= NAV_REVEAL_UP_PX) {
          concealLockedRef.current = false;
          setNavConcealedByScroll(false);
        }
        return;
      }

      if (concealLockedRef.current) {
        setNavConcealedByScroll(true);
      }
    };

    const onLenisScroll = (event) => {
      const { scroll, direction } = event.detail ?? {};
      if (typeof scroll !== "number") return;
      apply(scroll, direction ?? 0);
    };

    const onResize = () => {
      const y =
        typeof window.scrollY === "number" ? window.scrollY : window.pageYOffset;
      apply(y, 0);
    };

    window.addEventListener(LENIS_SCROLL_EVENT, onLenisScroll);
    window.addEventListener("resize", onResize);
    reduceMq.addEventListener("change", onResize);
    onResize();

    return () => {
      window.removeEventListener(LENIS_SCROLL_EVENT, onLenisScroll);
      window.removeEventListener("resize", onResize);
      reduceMq.removeEventListener("change", onResize);
    };
  }, []);

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeMobileMenu();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileMenuOpen, closeMobileMenu]);

  useEffect(() => {
    const onResize = () => {
      if (window.matchMedia("(min-width: 769px)").matches) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const mobileMenuPortal =
    typeof document !== "undefined"
      ? createPortal(
        <div
          className={`sp-nav-mobile-shell${mobileMenuOpen ? " sp-nav-mobile-shell--open" : ""}`}
          aria-hidden={!mobileMenuOpen}
        >
          <div
            className="sp-nav-mobile-backdrop"
            tabIndex={-1}
            data-testid="nav-mobile-backdrop"
            aria-hidden={true}
            onClick={closeMobileMenu}
          />
          <div
            id="sp-nav-mobile-drawer"
            className="sp-nav-mobile-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
          >
            <div className="sp-nav-mobile-drawer-header">
              <button
                type="button"
                className="sp-nav-drawer-close"
                aria-label="Close menu"
                data-testid="nav-drawer-close"
                onClick={closeMobileMenu}
              >
                <X size={24} strokeWidth={2.25} aria-hidden />
              </button>
            </div>
            <div className="sp-nav-mobile-drawer-links">
              {NAV_LINKS.map(({ href, label, testId }) => {
                const active = isNavLinkActive(href);
                return (
                  <a
                    key={href}
                    href={href}
                    className={`sp-nav-mobile-link${active ? " sp-nav-mobile-link--active" : ""}`}
                    data-testid={`${testId}-mobile`}
                    aria-current={active ? "location" : undefined}
                    onClick={closeMobileMenu}
                  >
                    {label}
                  </a>
                );
              })}
            </div>
            <div className="sp-nav-mobile-drawer-actions">
              <a
                href={getStudentLoginUrl()}
                className="sp-btn sp-btn-primary sp-nav-mobile-cta"
                data-testid="nav-cta-book-mobile"
                id="home-login-mobile"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
              >
                Login
              </a>
            </div>
          </div>
        </div>,
        document.body,
      )
      : null;

  const scrollConceal = !mobileMenuOpen && navConcealedByScroll;

  return (
    <>
      {/* Outside .sp-atmosphere (isolation: isolate); sibling stacks above Journey/Live/AI */}
      <nav
        className={`sp-nav${scrollConceal ? " sp-nav--scroll-hide" : ""}`}
        data-testid="hero-nav"
        aria-label="Primary"
      >
        <a
          href="/"
          className="sp-logo"
          data-testid="hero-logo"
          aria-label="Sparkl home"
        >
          <img
            className="sp-logo-img"
            src={sparklHeaderLogoUrl}
            alt="Sparkl"
            decoding="async"
          />
        </a>
        <div className="sp-nav-links">
          {NAV_LINKS.map(({ href, label, testId }) => {
            const active = isNavLinkActive(href);
            return (
              <a
                key={href}
                href={href}
                className={`sp-nav-link${active ? " sp-nav-link--active" : ""}`}
                data-testid={testId}
                aria-current={active ? "location" : undefined}
              >
                {label}
              </a>
            );
          })}
        </div>
        <div className="sp-nav-desktop-actions">
          <a
            href={getStudentLoginUrl()}
            className="sp-nav-link"
            data-testid="nav-link-login"
            style={{ display: "inline-block" }}
          >
            Login
          </a>
          <a
            href="/register"
            className="sp-btn sp-btn-primary registerLink"
            data-register-ref=""
            style={{ padding: "10px 18px", fontSize: 13.5 }}
            data-testid="nav-cta-book"
            id="home-register-consult-desktop"
            target="_blank"
            rel="noopener noreferrer"
          >
            Register
          </a>
        </div>
        <div className="sp-nav-mobile-tray">
          <a
            href="/register"
            className="sp-btn sp-btn-primary sp-nav-mobile-cta registerLink"
            data-register-ref=""
            data-testid="nav-cta-book-mobile"
            style={{ height: "44px" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            Register
          </a>
          <button
            type="button"
            className="sp-nav-toggle"
            aria-expanded={mobileMenuOpen}
            aria-controls="sp-nav-mobile-drawer"
            aria-label={
              mobileMenuOpen
                ? "Close navigation menu"
                : "Open navigation menu"
            }
            data-testid="nav-toggle"
            onClick={() => setMobileMenuOpen((o) => !o)}
          >
            {mobileMenuOpen ? (
              <X size={22} strokeWidth={2.25} aria-hidden />
            ) : (
              <Menu size={22} strokeWidth={2.25} aria-hidden />
            )}
          </button>
        </div>
      </nav>
      {mobileMenuPortal}
    </>
  );
}
